import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, LinearProgress } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { mockCreditReport } from '../../utils/mockData';
import SlideHeader from './SlideHeader';
import { useTheme } from '@mui/material/styles';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CreditAnalysisSlide: React.FC = () => {
  const theme = useTheme();
  const chartData = {
    labels: mockCreditReport.creditors.map(creditor => creditor.name),
    datasets: [
      {
        label: 'Credit Utilization (%)',
        data: mockCreditReport.creditors.map(creditor => creditor.utilization),
        backgroundColor: 'rgba(220, 53, 69, 0.8)',  
        borderColor: 'rgba(220, 53, 69, 1)',
        borderWidth: 1
      },
      {
        label: 'Available Credit (%)',
        data: mockCreditReport.creditors.map(creditor => 100 - creditor.utilization),
        backgroundColor: 'rgba(40, 167, 69, 0.8)',  
        borderColor: 'rgba(40, 167, 69, 1)',
        borderWidth: 1
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          color: '#495057',
          font: {
            size: 12,
            weight: 500
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        stacked: true,
        max: 100,
        ticks: {
          color: '#495057',
          font: {
            size: 12,
            weight: 500
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 20,
          font: {
            size: 13,
            weight: 500
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      title: {
        display: false
      },
    },
  };

  return (
    <Box>
      <SlideHeader title="Credit Analysis" />
      {/* Content Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ 
              p: { xs: 1, sm: 4 }, 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              width: '100%' 
            }}>
              <TableContainer sx={{ 
                maxWidth: '100%',
                width: '100%', 
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                mx: { xs: -1, sm: 0 }, 
                px: { xs: 1, sm: 0 }, 
                '&::-webkit-scrollbar': {
                  height: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#555'
                  }
                }
              }}>
                <Table sx={{ 
                  minWidth: { xs: 800, sm: 650 }, 
                  '& .MuiTableCell-root': {
                    px: { xs: 2, sm: 3 }, 
                    whiteSpace: 'nowrap' 
                  }
                }}>
                  <TableHead sx={{ 
                    bgcolor: '#0A1929',
                    '& .MuiTableCell-root': {
                      color: 'white',
                      borderBottom: 'none'
                    }
                  }}>
                    <TableRow>
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'left',
                        py: 2.5
                      }}>
                        Creditor
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        py: 2.5
                      }}>
                        Monthly Payment
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        py: 2.5
                      }}>
                        Balance
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        py: 2.5
                      }}>
                        Credit Limit
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        py: 2.5
                      }}>
                        Credit Utilization
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockCreditReport.creditors.map((creditor, index) => (
                      <TableRow 
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer',
                            '& .MuiTableCell-root': {
                              color: '#1976d2'
                            }
                          },
                          '& .MuiTableCell-root': {
                            borderColor: 'rgba(224, 224, 224, 0.4)'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" sx={{
                          fontWeight: 700,
                          color: '#0A1929',
                          textAlign: 'left',
                          py: 2
                        }}>
                          {creditor.name}
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          textAlign: 'center',
                          py: 2
                        }}>
                          ${creditor.monthlyPayment.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2 }}>
                          ${creditor.balance.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2 }}>
                          ${creditor.creditLimit.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ 
                          textAlign: 'center',
                          py: 2,
                          color: creditor.utilization > 75 ? 'error.main' : 'inherit'
                        }}>
                          {creditor.utilization}%
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                          transition: 'background-color 0.2s ease'
                        },
                        '& .MuiTableCell-root': {
                          borderTop: '2px solid rgba(224, 224, 224, 0.8)',
                          py: 2.5
                        }
                      }}
                    >
                      <TableCell sx={{ textAlign: 'left' }}>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <strong>${mockCreditReport.totalMonthlyPayment.toLocaleString()}</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <strong>${mockCreditReport.totalBalance.toLocaleString()}</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <strong>${mockCreditReport.totalCreditLimit.toLocaleString()}</strong>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <strong>{mockCreditReport.creditUtilization}%</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Paper sx={{ 
                  p: { xs: 2, sm: 4 }, 
                  mb: 3, 
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  overflow: 'hidden'
                }}>
                  <Typography variant="h5" sx={{ 
                    mb: 3, 
                    color: '#0A1929',
                    fontWeight: 600,
                    textAlign: 'center',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    Overall Credit Utilization
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 2, sm: 4 }, 
                    mb: 3,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }, 
                    width: '100%'
                  }}>
                    <Box sx={{ 
                      textAlign: 'center',
                      flex: { xs: '1 1 100%', sm: '0 1 auto' } 
                    }}>
                      <Typography variant="h3" sx={{ 
                        color: mockCreditReport.creditUtilization > 75 ? 'error.main' : '#0A1929',
                        fontWeight: 600,
                        mb: 1,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } 
                      }}>
                        {mockCreditReport.creditUtilization}%
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '1rem' } 
                      }}>
                        Current Utilization
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      height: { xs: '2px', sm: '60px' },
                      width: { xs: '80%', sm: '2px' },
                      borderLeft: { sm: '2px solid rgba(0, 0, 0, 0.1)' },
                      borderTop: { xs: '2px solid rgba(0, 0, 0, 0.1)', sm: 'none' },
                      margin: { xs: '12px auto', sm: 0 }
                    }} />
                    <Box sx={{ 
                      textAlign: 'center',
                      flex: { xs: '1 1 100%', sm: '0 1 auto' } 
                    }}>
                      <Typography variant="h3" sx={{ 
                        color: '#2E7D32',
                        fontWeight: 600,
                        mb: 1,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } 
                      }}>
                        30%
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '1rem' } 
                      }}>
                        Target Utilization
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ 
                    color: '#d32f2f',
                    textAlign: 'center',
                    mt: 1,
                    fontWeight: 500
                  }}>
                    This is currently damaging your credit score of 530
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={mockCreditReport.creditUtilization} 
                    sx={{
                      height: 12,
                      borderRadius: 2,
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: mockCreditReport.creditUtilization > 75 ? 'error.main' : '#2E7D32',
                        transition: 'transform 0.4s ease'
                      }
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={7}>
                <Paper sx={{ 
                  p: 4, 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                }}>
                  <Typography variant="h5" sx={{ 
                    mb: 1,
                    color: '#0A1929',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}>
                    Credit Utilization by Account
                  </Typography>
                  <Box sx={{ 
                    width: '100%', 
                    height: '300px',
                    position: 'relative',
                    '&:hover': {
                      '& canvas': {
                        transition: 'all 0.3s ease',
                        transform: 'scale(1.01)'
                      }
                    }
                  }}>
                    <Bar data={chartData} options={{
                      ...chartOptions,
                      aspectRatio: 2,
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          ...chartOptions.plugins.legend,
                          labels: {
                            ...chartOptions.plugins.legend.labels,
                            padding: 15
                          }
                        }
                      }
                    }} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CreditAnalysisSlide;
